import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { TbPhotoEdit } from "react-icons/tb";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { Button, Card, CardBody, CardFooter } from "@material-tailwind/react";
import { useDispatch, useSelector } from "react-redux";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { AccountProfile, AccountProfiledit } from "../../redux/ApiSlice/AccountProfileSlice";
import { BeatLoader } from "react-spinners";
import { IoIosArrowDown } from "react-icons/io";

export default function ProfileEdit() {
    const menuRef = useRef(null);
    const dispatch = useDispatch();

    const fileInputRef = useRef(null);
    const initializedRef = useRef(false);
    const navigateTimerRef = useRef(null);
    const ProfileDetails = useSelector((state) => state.AccountProfile);
    const { User_profile } = ProfileDetails;
    const [userDetails, setUserDetails] = useState({});
    const [image, setImage] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const navigate = useNavigate();

    useOutsideClick(menuRef, () => setShowConfirm(false));

    const getUserDetails = useCallback(async () => {
        await dispatch(AccountProfile());
    }, [dispatch]);


    const handleChange = (e) => {
        setIsDirty(true);
        setUserDetails({
            ...userDetails,
            [e.target.name]: e.target.value,
        });
        if (fieldErrors[e.target.name]) {
            setFieldErrors((prev) => ({ ...prev, [e.target.name]: "" }));
        }
    };

    const handleBack = () => {
        const isFormDirty =
            JSON.stringify(userDetails) !== JSON.stringify(User_profile);
        if (isFormDirty) {
            setShowConfirm(true);
        } else {
            navigate(-1);
        }
    };

    // Warn user before leaving the page if there are unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (isDirty) {
                event.preventDefault();
                event.returnValue =
                    "You have unsaved changes. Do you really want to leave?";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [isDirty]);

    const handleIconClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setIsDirty(true);
            setUserDetails({
                ...userDetails,
                avatar: file,
            });
            setImage(URL.createObjectURL(file));
        }
    };

    useEffect(() => {
        return () => {
            if (image?.startsWith("blob:")) {
                URL.revokeObjectURL(image);
            }
        };
    }, [image]);

    const handleSubmit = async () => {
        const formData = new FormData();
        formData.append("full_name", userDetails.full_name ?? "");
        formData.append("username", userDetails.username ?? "");
        formData.append("about_us", userDetails.about_us ?? "");
        formData.append("portfolio", userDetails.portfolio ?? "");
        formData.append("gender", userDetails.gender ?? "");

        if (userDetails.avatar instanceof File) {
            formData.append("avatar", userDetails.avatar);
        }
        setIsLoading(true);
        setFieldErrors({});
        const response = await dispatch(AccountProfiledit(formData));
        toast.dismiss();
        if (response.meta.requestStatus === "rejected") {
            const errorData = response.payload || {};
            const newFieldErrors = {};
            let hasRestrictedWords = false;
            Object.entries(errorData).forEach(([field, messages]) => {
                if (Array.isArray(messages) && messages.some((m) => typeof m === "string" && m.toLowerCase().includes("restricted"))) {
                    newFieldErrors[field] = "Inappropriate language is not allowed on your profile.";
                    hasRestrictedWords = true;
                }
            });
            if (hasRestrictedWords) {
                setFieldErrors(newFieldErrors);
            } else {
                toast.error("Failed to update");
            }
            setIsLoading(false);
        } else {
            toast.success(response?.payload?.message || "Profile updated successfully");
            setShowConfirm(false);
            setIsDirty(false);
            navigateTimerRef.current = setTimeout(() => {
                navigate("/account");
            }, 2000);
        }
    };


    useEffect(() => {
        getUserDetails();
    }, [getUserDetails]);

    useEffect(() => {
        if (User_profile && !initializedRef.current) {
            setUserDetails(User_profile);
            setImage(User_profile.avatar);
            initializedRef.current = true;
        }
    }, [User_profile]);

    useEffect(() => {
        return () => {
            if (navigateTimerRef.current) {
                clearTimeout(navigateTimerRef.current);
            }
        };
    }, []);

    return (
        <div className="h-full w-full bg-white dark:bg-[#1E1D21] relative overflow-y-scroll">
            {/* <Toaster position="top-right" /> */}
            <div className="grid grid-cols-12 h-12 bg-[#BDE6FF] rounded-b-xl justify-center items-center w-full gap-x-4 z-50 sticky top-0 py-2">
                <div className="col-span-2">
                    <IoMdArrowBack
                        onClick={() => handleBack()}
                        className="text-[24px] text-black dark:text-black cursor-pointer ml-2"
                    />
                </div>
                <h1 className="col-span-8 text-black dark:text-black text-[18px] font-bold text-center">
                    Edit Profile
                </h1>
                <div className="col-span-2" />
            </div>
            <div className="grid grid-cols-12 px-2 mt-4 z-10">
                <div className="col-span-12 flex flex-col justify-center items-center my-4">
                    <div className="relative group">
                        <img
                            alt="IMG"
                            src={image}
                            onClick={handleIconClick}
                            className="h-[6rem] w-[6rem] rounded-[50%] object-cover"
                        />
                        <div onClick={handleIconClick} className="absolute inset-0 z-10 cursor-pointer items-center justify-center hidden h-[6rem] w-[6rem] rounded-[50%] object-cover transition-all duration-300 bg-black/10 group-hover:flex">
                            <button className="flex items-center justify-center h-4 w-4 text-center rounded-full ring-1 ring-transparent ">
                                <TbPhotoEdit className="text-white" />
                            </button>
                        </div>
                        <input
                            type="file"
                            ref={fileInputRef}
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={handleFileChange}
                        />
                    </div>
                    <div
                        onClick={handleIconClick}
                        className="mt-1 text-black dark:text-white text-[14px] font-bold cursor-pointer"
                    >
                        Change Profile Picture
                    </div>
                </div>
            </div>
            <div className="flex flex-col mb-2.5 px-2">
                <p className="text-[12px] text-black dark:text-white font-extrabold mb-0.5">
                    Name
                </p>
                <input
                    type="text"
                    name="full_name"
                    onChange={handleChange}
                    placeholder="Enter your name"
                    value={userDetails?.full_name ?? ""}
                    className={`w-full px-2 py-1.5 focus:outline-none rounded-md border text-[14px] bg-white dark:bg-[#333333] dark:text-white ${fieldErrors.full_name ? "border-red-500" : "border-gray-400 dark:border-gray-800/50"}`}
                />
                {fieldErrors.full_name && <p className="text-red-500 text-[12px] mt-1">{fieldErrors.full_name}</p>}
            </div>
            <div className="flex flex-col mb-2.5 px-2">
                <p className=" text-[12px] text-black dark:text-white font-extrabold mb-0.5">
                    Username
                </p>
                <input
                    type="text"
                    name="username"
                    onChange={handleChange}
                    placeholder="Enter your username"
                    value={userDetails?.username ?? ""}
                    className={`w-full px-2 py-1.5 focus:outline-none rounded-md border text-[14px] bg-white dark:bg-[#333333] dark:text-white ${fieldErrors.username ? "border-red-500" : "border-gray-400 dark:border-gray-800/50"}`}
                />
                {fieldErrors.username && <p className="text-red-500 text-[12px] mt-1">{fieldErrors.username}</p>}
            </div>
            <div className="flex flex-col mb-2.5 px-2">
                <p className="text-[12px] text-black dark:text-white font-extrabold mb-0.5">Bio</p>
                <textarea
                    type="text"
                    name="about_us"
                    rows={3}
                    onChange={handleChange}
                    placeholder="Bio..."
                    value={userDetails?.about_us ?? ""}
                    className={`w-full px-2 py-1.5 focus:outline-none rounded-md border text-[14px] leading-5 resize-none bg-white dark:bg-[#333333] dark:text-white ${fieldErrors.about_us ? "border-red-500" : "border-gray-400 dark:border-gray-800/50"}`}
                />
                {fieldErrors.about_us && <p className="text-red-500 text-[12px] mt-1">{fieldErrors.about_us}</p>}
            </div>
            <div className="flex flex-col w-[30%] mb-2.5 px-2 relative">
                <p className="text-[12px] text-black dark:text-white font-extrabold mb-0.5">
                    Gender
                </p>
                <select
                    name="gender"
                    id="underline_select"
                    onChange={handleChange}
                    value={userDetails?.gender ?? ""}
                    className="w-full px-2 py-1.5 focus:outline-none rounded-md border border-gray-400 dark:border-gray-800/50 text-[14px] block appearance-none cursor-pointer bg-white dark:bg-[#333333] dark:text-white"
                >
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                    <option value="O">Other</option>
                </select>
                <IoIosArrowDown className="absolute right-4 top-[30px] pointer-events-none cursor-pointer" />
            </div>
            <div className="flex flex-col mb-2.5 px-2">
                <p className="text-[12px] text-black dark:text-white font-extrabold mb-0.5">Links</p>
                <input
                    type="url"
                    value={userDetails?.portfolio ?? ""}
                    onChange={handleChange}
                    name="portfolio"
                    placeholder="https://www.example.com"
                    className={`w-full px-2 py-1.5 focus:outline-none rounded-md border text-[14px] bg-white dark:bg-[#333333] dark:text-white ${fieldErrors.portfolio ? "border-red-500" : "border-gray-400 dark:border-gray-800/50"}`}
                />
                {fieldErrors.portfolio && <p className="text-red-500 text-[12px] mt-1">{fieldErrors.portfolio}</p>}
            </div>
            <div className="flex items-center justify-center mb-2.5">
                <Button
                    onClick={() => handleSubmit()}
                    disabled={isLoading}
                    className="w-[50%] py-1.5 rounded-[5px] bg-[#333333] text-white text-[12px]"
                >
                    {isLoading ? (
                        <BeatLoader
                            size={10}
                            color={"#fff"}
                            loading={isLoading}
                        />
                    ) : (
                        "Save"
                    )}

                </Button>
            </div>

            {showConfirm && (
                <div className="absolute flex justify-center items-center top-0 h-full w-full">
                    <div className="absolute h-screen w-full top-0 opacity-100 bg-opacity-50 bg-gray-700 z-20"></div>
                    <Card className="w-[90%] px-2 py-3 absolute  z-50" ref={menuRef}>
                        <CardBody className="text-[14px] p-0 text-center">
                            If You want to save the changes click on the save button
                        </CardBody>
                        <CardFooter className="flex justify-center items-center gap-4 w-full p-0 mt-2">
                            <Button
                                className="px-2 py-2 w-14 bg-gray-400 text-white text-[10px]"
                                onClick={() => navigate(-1)}
                            >
                                cancel
                            </Button>
                            <Button
                                className="px-2 py-2 w-14 bg-[#333333] text-white text-[10px]"
                                onClick={() => handleSubmit()}
                            >
                                Save
                            </Button>
                        </CardFooter>
                    </Card>
                </div>
            )}
        </div>
    );
}
